import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';

import { Box, Divider, getSemanticValue, Headline, Search, Text, Spaces } from '@freenow/wave';

import { BusinessAccount } from '../api/central-organisations.api';
import { useFormatMessage } from '../hooks/useFormatMessage';
import { BusinessAccountContext } from '../context/BusinessAccountContext';
import { useDebounce } from '../hooks/useDebounce';
import { SESSION_STORAGE_KEYS } from '../constants/sessionStorageKeys';
import { CenteredLoadingIndicator } from './CenteredLoadingIndicator';
import { RemotePagination } from './RemotePagination';

const ClickableBox = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${Spaces[1]};
    margin-bottom: ${Spaces[1]};

    &:hover,
    &:focus {
        background-color: ${getSemanticValue('background-element-neutral-emphasized')};
    }
`;

const ScrollableList = styled.div`
    height: 280px;
    overflow: auto;
`;

interface Props {
    onSelect: () => void;
    filterCurrentBusinessAccount?: boolean;
    filter?: (businessAccount: BusinessAccount) => boolean;
}

export const BusinessAccountSelectorList = ({ onSelect, filterCurrentBusinessAccount = true, filter }: Props) => {
    const formatMessage = useFormatMessage();
    const {
        currentBusinessAccount,
        setCurrentBusinessAccountId,
        setSearch,
        totalPages,
        page,
        setPage,
        searchedTotalBusinessAccounts,
        coBusinessAccounts,
        loadingCentralOrganisationBusinessAccounts,
    } = useContext(BusinessAccountContext);
    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounce(searchInput);

    useEffect(() => {
        setSearch(debouncedSearch);
    }, [debouncedSearch, setSearch]);

    const handleBusinessAccountChange = (newBusinessAccountId) => {
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.GTC_POPUP_DISABLED);
        setCurrentBusinessAccountId(newBusinessAccountId);
        setPage(0);
        setSearchInput('');
        onSelect();
    };

    const renderBusinessAccountList = () => {
        if (loadingCentralOrganisationBusinessAccounts || !coBusinessAccounts) {
            return <CenteredLoadingIndicator />;
        }

        if (coBusinessAccounts.length === 0 && searchInput.length) {
            return (
                <Text as="p" textAlign="center">
                    <FormattedMessage id="businessAccountSwitcher.noResults" />
                </Text>
            );
        }

        let renderedBusinessAccounts = coBusinessAccounts;
        if (filterCurrentBusinessAccount) {
            renderedBusinessAccounts = renderedBusinessAccounts.filter(
                (businessAccount) => businessAccount?.id !== currentBusinessAccount?.id,
            );
        }

        if (filter) {
            renderedBusinessAccounts = renderedBusinessAccounts.filter(filter);
        }

        return renderedBusinessAccounts.map((businessAccount) => (
            <ClickableBox
                role="button"
                tabIndex={0}
                onClick={() => handleBusinessAccountChange(businessAccount.id)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.keyCode === 13) {
                        e.preventDefault();
                        handleBusinessAccountChange(businessAccount.id);
                    }
                }}
                key={businessAccount.id}
            >
                <Box display="flex" flexDirection="column">
                    <Headline as="h5">{businessAccount.name}</Headline>
                    {businessAccount.inAppName && businessAccount.name !== businessAccount.inAppName ? (
                        <Text fontSize={1}>({businessAccount.inAppName})</Text>
                    ) : null}
                </Box>
                {businessAccount.legalEntityDetails &&
                businessAccount.legalEntityDetails.name !== businessAccount.name ? (
                    <Text as="p" fontSize={0} textAlign="right">
                        {businessAccount.legalEntityDetails.name}
                    </Text>
                ) : null}
            </ClickableBox>
        ));
    };

    return (
        <>
            <Headline as="h2" mb={1}>
                <FormattedMessage id="businessAccountSelector.headline" />
            </Headline>
            <Text as="p" fontSize={1} mb={3}>
                <FormattedMessage id="businessAccountSelector.description" />
            </Text>
            <Search
                width="100%"
                value={searchInput}
                onInputChange={(value) => setSearchInput(value)}
                onClear={() => setSearchInput('')}
                placeholder={formatMessage('businessAccountSelector.searchPlaceholder')}
            />
            <Divider />

            <ScrollableList>{renderBusinessAccountList()}</ScrollableList>

            {totalPages && totalPages > 1 ? (
                <RemotePagination
                    index={page}
                    onIndexChange={(newIndex) => setPage(newIndex)}
                    totalElements={searchedTotalBusinessAccounts || 0}
                    totalPages={totalPages || 0}
                    pageSize={20}
                />
            ) : null}
        </>
    );
};
